import { Container, MenuContainer, MenuButton, MenuDropdown, MenuItem } from "./styles";
import { useState, useEffect, useRef } from "react";
import api from "../../../../../../../../services/api";
import { comum } from "../../../../../../../../styles/Themes/light";
import { IconUser } from "../../../../../../../icons";
import ProfessionalSelector from "../../../../../../../ProfessionalSelector";
import Skeleton from "react-loading-skeleton";
import ModalContatoProfissional from "../../../../../../Common/ModalContato/ModalContatoProfissional";
import { useAuth } from "../../../../../../../../contexts/UserContext";
import { usuarioEhCorrespondente, podeEditarDemanda } from "../../../../../../../../helpers/permissoesUsuario";
import { MoreVertical, Eye, ArrowRightLeft } from "lucide-react";

interface CardParteProfissionalProps {
    demanda: any;
    onRecarregarDemanda?: () => void;
}

export default function CardParteProfissional({demanda, onRecarregarDemanda}: CardParteProfissionalProps) {
    const { user } = useAuth();
    const [profissional, setProfissional] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [modalContato, setModalContato] = useState(false);
    const [abrirModalTrocar, setAbrirModalTrocar] = useState(false);
    const [menuAberto, setMenuAberto] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
    const idUsuarioCarregadoRef = useRef<number | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    async function carregaProfissional(id_usuario?: number) {
        if(!id_usuario) id_usuario = demanda?.id_usuario
        if(!id_usuario) return;

        // Evita recarregar se o mesmo id_usuario já foi carregado
        if (idUsuarioCarregadoRef.current === id_usuario) {
            return;
        }

        setLoading(true);
        try {
            const response = await api.get('usuario/' + id_usuario);
            if (response.data.status === 'success') {
                setProfissional(response.data.result);
                idUsuarioCarregadoRef.current = id_usuario;
            }
        } catch (error) {
            console.error('Erro ao carregar profissional:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleProfissionalAlterado = async (id_usuario?: number) => {
        await carregaProfissional(id_usuario);
        setAbrirModalTrocar(false);
        if (onRecarregarDemanda) {
            onRecarregarDemanda();
        }
    };

    const statusPermitidosAlteracao = ['Em Contratação', 'Aguardando Execução', 'Em Execução'];
    const podeAlterarProfissional = podeEditarDemanda(user) && demanda?.status && statusPermitidosAlteracao.includes(demanda.status);
    const mostrarBotaoAdicionar = !profissional && podeAlterarProfissional && demanda?.id_proposta;
    const mostrarMenuAcoes = profissional && !usuarioEhCorrespondente(user);

    useEffect(() => {
        const idUsuario = demanda?.id_usuario;
        if(idUsuario && idUsuario !== 0 && idUsuarioCarregadoRef.current !== idUsuario) {
            carregaProfissional(idUsuario);
        } else if (!idUsuario || idUsuario === 0) {
            setProfissional(null);
            idUsuarioCarregadoRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demanda?.id_usuario]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuAberto(false);
                setMenuPosition(null);
            }
        };

        const handleScroll = () => {
            if (menuAberto && menuButtonRef.current) {
                const rect = menuButtonRef.current.getBoundingClientRect();
                setMenuPosition({
                    top: rect.bottom + 8,
                    right: window.innerWidth - rect.right
                });
            }
        };

        if (menuAberto) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [menuAberto]);

    const handleAbrirMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (menuButtonRef.current) {
            const rect = menuButtonRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right
            });
        }
        setMenuAberto(!menuAberto);
    };

    const handleVisualizar = () => {
        setMenuAberto(false);
        setMenuPosition(null);
        setModalContato(true);
    };

    const handleTrocar = () => {
        setMenuAberto(false);
        setMenuPosition(null);
        setAbrirModalTrocar(true);
    };

    const handleModalTrocarClose = () => {
        setAbrirModalTrocar(false);
    };

    return (
        <Container>
            {loading && (
                <div className="parte-select">
                    <Skeleton height={40} width="100%" />
                </div>
            )}

            {!loading && (
                <>
                <span className="label">Profissional</span>

                {profissional && (
                    <>
                        <div className="parte">
                            <div className="iconc">
                                <IconUser color={comum.colors.primaryRed400}/>
                            </div>
                            <div className="info">
                                <span className="nome">{profissional?.nome?.split(' ')[0] || 'N/A'}</span>
                                <span className="codigo">D{profissional?.id_usuario || ''}</span>
                            </div>
                            {mostrarMenuAcoes && (
                                <MenuContainer ref={menuRef}>
                                    <MenuButton
                                        ref={menuButtonRef}
                                        onClick={handleAbrirMenu}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleAbrirMenu(e as any);
                                            }
                                        }}
                                        aria-label="Menu de ações"
                                    >
                                        <MoreVertical size={18} />
                                    </MenuButton>
                                    {menuAberto && menuPosition && (
                                        <MenuDropdown style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}>
                                            <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleVisualizar();
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleVisualizar();
                                                    }
                                                }}
                                            >
                                                <Eye size={16} />
                                                Visualizar
                                            </MenuItem>
                                            {podeAlterarProfissional && demanda?.id_proposta && (
                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTrocar();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleTrocar();
                                                        }
                                                    }}
                                                >
                                                    <ArrowRightLeft size={16} />
                                                    Trocar
                                                </MenuItem>
                                            )}
                                        </MenuDropdown>
                                    )}
                                </MenuContainer>
                            )}
                        </div>
                        {!usuarioEhCorrespondente(user) && (
                            <ModalContatoProfissional profissional={profissional} modalAtivo={modalContato} setModalAtivo={setModalContato} />
                        )}
                        {podeAlterarProfissional && demanda?.id_proposta && (
                            <ProfessionalSelector
                                id_proposta={demanda.id_proposta}
                                selectedProfessional={profissional}
                                onSelectProfessional={handleProfissionalAlterado}
                                isAlteracao={true}
                                openModalExternally={abrirModalTrocar}
                                onModalClose={handleModalTrocarClose}
                                className="hidden-selector"
                            />
                        )}
                    </>
                )}

                {mostrarBotaoAdicionar && (
                    <div className="parte-select">
                        <ProfessionalSelector
                            id_proposta={demanda?.id_proposta}
                            selectedProfessional={profissional}
                            onSelectProfessional={handleProfissionalAlterado}
                        />
                    </div>
                )}

                {!profissional && !mostrarBotaoAdicionar && (
                    <div className="parte-vazio">Nenhum profissional atribuído</div>
                )}
            </>
            )}
        </Container>
    )
}